import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type {
  AddressAutocompleteRequest,
  AddressAutocompleteResponse,
  AddressExpressAutocompleteRequest,
  AddressExpressAutocompleteResponse,
} from '@appTypes/precisely';
import { getPreciselyUrl } from '@config';

import { tagTypes } from './constants';
import { esbApi } from './esb-api';

export const preciselyApi = createApi({
  reducerPath: 'preciselyApi',
  tagTypes: Object.keys(tagTypes),
  baseQuery: fetchBaseQuery({ baseUrl: getPreciselyUrl() }),
  endpoints: builder => ({
    getPreciselyCassAddress: builder.query<
      AddressAutocompleteResponse,
      {
        body: AddressAutocompleteRequest;
        transactionId: string;
      }
    >({
      queryFn: async (arg, queryApi, _extraOptions, baseQuery) => {
        try {
          const auth = await queryApi
            .dispatch(
              esbApi.endpoints.authenticatePrecisely.initiate({
                params: {
                  skipCache: false,
                  scope: 'default',
                },
              }),
            )
            .unwrap();

          const autocompleteResponse = await baseQuery({
            url: '/v1/autocomplete',
            method: 'POST',
            headers: {
              Authorization: `Bearer ${auth.accessToken}`,
              'x-transaction-id': arg.transactionId,
            },
            body: arg.body,
          });

          if (autocompleteResponse.error) {
            return { error: autocompleteResponse.error };
          }

          return {
            data: autocompleteResponse.data as AddressAutocompleteResponse,
          };
        } catch (error) {
          console.warn('error', error);
          return {
            data: {
              response: {
                status: '400',
                predictions: [],
              },
            },
          };
        }
      },
      providesTags: (result, error) =>
        result && !error
          ? [
              {
                type: tagTypes.CASSAutocomplete,
              },
            ]
          : [],
    }),

    getPreciselyExpressCassAddress: builder.query<
      AddressExpressAutocompleteResponse,
      {
        body: AddressExpressAutocompleteRequest;
        transactionId: string;
      }
    >({
      queryFn: async (arg, queryApi, _extraOptions, baseQuery) => {
        try {
          const auth = await queryApi
            .dispatch(
              esbApi.endpoints.authenticatePrecisely.initiate({
                params: {
                  skipCache: false,
                  scope: 'default',
                },
              }),
            )
            .unwrap();

          const autocompleteResponse = await baseQuery({
            url: '/v2/express-autocomplete',
            method: 'POST',
            headers: {
              Authorization: `Bearer ${auth.accessToken}`,
              'x-transaction-id': arg.transactionId,
            },
            body: arg.body,
          });

          if (autocompleteResponse.error) {
            return { error: autocompleteResponse.error };
          }

          return {
            data: autocompleteResponse.data as AddressExpressAutocompleteResponse,
          };
        } catch (error) {
          console.warn('error', error);
          return {
            data: {
              response: {
                status: '400',
                predictions: [],
              },
            },
          };
        }
      },
      providesTags: (result, error) =>
        result && !error
          ? [
              {
                type: tagTypes.CASSAutocomplete,
              },
            ]
          : [],
    }),
    // add here new endpoints
  }),
});

export const { useLazyGetPreciselyExpressCassAddressQuery } = preciselyApi;
